/* eslint-disable brace-style */
/* eslint-disable react/no-array-index-key */
import React, { useState, useRef, useEffect } from 'react';
import { Grid } from '@mui/system';
import { Button, Box } from '@mui/material';
import Paper from '@mui/material/Paper';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ paddingLeft: '80px' }}
      >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// Custom dropdown menu component
function CustomDropdown({ options, isOpen, onClose }) {
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !options) return null;

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: '100%',
        left: '0',
        zIndex: 100,
        minWidth: '154px',
        maxWidth: '240px',
        backgroundColor: '#fff',
        borderRadius: '4px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
        marginTop: '8px',
      }}
    >
      {options?.map((option, index) => (
        <div
          key={index}
          style={{
            padding: '10px 16px',
            cursor: 'pointer',
            backgroundColor: '#fff',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#fff';
          }}
        >
          <Box>
            <a
              href={option.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textDecoration: 'none',
                fontFamily: 'Lato',
                fontWeight: 400,
                fontSize: '16px',
                color: '#114559',
                lineHeight: '20px',
              }}
              >
              <img
                src={`${option?.icon?.url}`}
                alt={option?.icon?.name}
              />
              <span style={{
                overflow: 'hidden',
                lineClamp: '1',
                display: '-webkit-box',
                '-webkitLineClamp': ' 1',
                '-webkitBoxOrient': 'vertical',
              }}
              >
                {option?.item}
              </span>
            </a>
          </Box>
        </div>
      ))}
    </div>
  );
}

function Hero({ heroSection, headerAction }) {
  const navigate = useNavigate();
  const [activeHeroCard, setActiveHeroCard] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');

  // Handle header actions (login/signup)
  useEffect(() => {
    if (headerAction) {
      // If login is clicked from header
      if (headerAction === 'login') {
        setSelectedRole('farm');
        setDropdownOpen(true);
      }
      // If signup is clicked from header
      else if (headerAction === 'sign-up') {
        setSelectedRole('clinic');
        setDropdownOpen(true);
      }
    }
  }, [headerAction]);

  const handleClick = (role) => {
    setSelectedRole(role);
    setDropdownOpen(true);
  };

  const handleClose = () => {
    setDropdownOpen(false);
  };

  const handleNavigation = (url) => {
    navigate(url);
  };

  // Always define menu items outside JSX to prevent re-renders
  const menuOptions = {
    farm: heroSection?.farmButton?.menuList,
    clinic: heroSection?.clinicButton?.menuList,
  };

  return (
    <Grid
      container
      className="hero-section"
      sx={{ justifyContent: 'center' }}
      style={{ backgroundImage: `url(${heroSection?.backgroundImage?.url})` }}
    >
      <Grid size={{ xs: 12, sm: 9, lg: 9 }}>
        <h1 className="title">{heroSection?.heading}</h1>
        <h5 className="subTitle">{heroSection?.subHeading}</h5>
      </Grid>
      <Grid
        mt={4}
        container
        size={{ xs: 12 }}
        sx={{
          justifyContent: 'center',
          gap: '24px',
        }}
      >
        <div style={{ position: 'relative' }}>
          <Button
            size="large"
            variant="contained"
            onClick={() => handleClick('farm')}
            sx={{ minWidth: '154px' }}
          >
            {heroSection?.farmButton?.label?.slice(0, 25)}
          </Button>
          <CustomDropdown
            options={menuOptions[selectedRole]}
            isOpen={dropdownOpen && selectedRole === 'farm'}
            onClose={handleClose}
          />
        </div>

        <div style={{ position: 'relative' }}>
          <Button
            size="large"
            variant="outlined"
            onClick={() => handleClick('clinic')}
            sx={{ minWidth: '154px', '&:hover': { backgroundColor: '#114559' } }}
          >
            {heroSection?.clinicButton?.label?.slice(0, 25)}
          </Button>
          <CustomDropdown
            options={menuOptions[selectedRole]}
            isOpen={dropdownOpen && selectedRole === 'clinic'}
            onClose={handleClose}
          />
        </div>
      </Grid>
      <Grid size={{ xs: 12, md: 10.5 }} mt={{ xs: 13, md: 8 }}>
        <Paper elevation={0} sx={{ padding: { xs: '20px 12px', md: '58px' } }} className="hero-card-wrap">
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
            }}>
            <Grid size={{ xs: 12, sm: 6.5 }}>
              <h2 className="box-title">{heroSection?.cardContent?.title}</h2>
              <p className="box-subtitle">
                {heroSection?.cardContent?.subTitle}
              </p>
            </Grid>
            <Grid
              container
              sm={4}
              sx={{
                justifyContent: 'flex-end',
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              <Button size="large" variant="contained" onClick={() => handleNavigation('/about')}>{heroSection?.cardContent?.buttonLabel?.slice(0, 25)}</Button>
            </Grid>
          </Grid>
          <Grid container mt={3} spacing={2}>
            {heroSection?.cardContent?.cardList?.map((i, idx) => (
              <Grid size={{ xs: 6, md: 3 }} key={idx}>
                <div
                  className={clsx(
                    'card-wrap',
                    activeHeroCard === idx && 'active',
                  )}
                  onMouseEnter={() => setActiveHeroCard(idx)}
                >
                  <div className={clsx(
                    'card-heading',
                    activeHeroCard === idx && 'active-content',
                  )}>
                    {i?.title}
                  </div>
                  <div className={clsx(
                    'card-description',
                    activeHeroCard === idx && 'active-content',
                  )}>{i?.description}
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>
          <Grid
            container
            sx={{
              justifyContent: 'center',
              display: { xs: 'flex', sm: 'none' },
              marginTop: '16px ',
            }}
          >
            <Button size="large" variant="contained">{heroSection?.cardContent?.buttonLabel?.slice(0, 25)}</Button>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default Hero;
